"use client"

import { useRef, useState } from "react"
import { useRouter } from "next/navigation"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/molecules/Avatar"

import useClickOutside from "@/hooks/useClickOutside"
import { Button } from "@/components/atoms/Button/button"
import { HeaderTranslations } from "@/app/hooks/useTranslation"
import { useSession } from "@/contexts/SessionContext"

interface AvatarDropdownProps {
  user: {
    id: string
    name?: string | null
    email?: string | null
    phone?: string | null
    image?: string | null
    roles?: string[] | null
  }
  translations: HeaderTranslations
}

const AvatarDropdown = ({ user, translations }: AvatarDropdownProps) => {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const router = useRouter()
  const { refreshSession } = useSession()
  useClickOutside(wrapperRef, () => {
    setOpen(false)
  })

  if (!user) return null

  return (
    <div className="relative z-50 inline-block text-left">
      <button
        type="button"
        className="inline-flex w-full justify-center rounded-md bg-inherit px-4 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-offset-2"
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(!open)}
      >
        <Avatar className="h-8 w-8">
          <AvatarImage src={user.image || ""} alt={user.name || "User avatar"} />
          <AvatarFallback>
            {user.name ? user.name.charAt(0).toUpperCase() : "U"}
          </AvatarFallback>
        </Avatar>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-900"
          ref={wrapperRef}
        >
          <div className="py-1">
            <div className="flex items-center px-4 py-2">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user.image || ""} alt={user.name || "User"} />
                <AvatarFallback>
                  {user.name ? user.name.substring(0, 2).toUpperCase() : "U"}
                </AvatarFallback>
              </Avatar>
              <div className="ml-3 overflow-hidden text-ellipsis">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user.email || user.phone || 'No contact info'}
                </p>
                {user.roles && (
                  <span className="mt-1 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    {Array.isArray(user.roles) ? user.roles[0] : String(user.roles).replace(/-/g, " ")}
                  </span>
                )}
              </div>
            </div>
            <hr className="border-t border-gray-100 dark:border-gray-600" />
            {!Array.isArray(user.roles) || !user.roles.includes("ADMIN") ? (
              <a href="/dashboard/user-settings" className="block px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600">
                {translations.setting}
              </a>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
              onClick={async () => {
                const { clearUser } = (await import("@/stores/user")).useUserStore.getState()
                const { clearLocationData } = (await import("@/stores/places")).usePlaceSearchStore.getState()
                
                await fetch("/api/auth/signout", { method: "POST" })
                clearUser()
                clearLocationData()
                await refreshSession()
                router.refresh() // Force server components to re-render with cleared cookies
                router.push("/")
              }}
            >
              {translations.logout}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AvatarDropdown
